"use client";

import Image from "next/image";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  RangeTournamentLeaderboardWidget,
  TournamentLeaderboardWidget,
} from "../queries/tournament-leaderboard";
import { GOLF_PORTAL_API } from "@/utils/constants";
import { TournamentBase } from "@/queries/tournaments";
import { Tournament } from "@/types/tournament";
import { useMemo, useState } from "react";
import Dropdown, { Option } from "./Dropdown";
import Link from "next/link";
import clsx from "clsx";
import { getTournamentPath } from "@/utils/tournament-path";

interface HeaderLeaderboardProps {
  title: string;
  btnTitle: string;
  btnUrl: string;
  logo: { filename: string; alt: string };
  defaultTournamentId: string;
  tournaments?: Tournament[];
}

const HeaderLeaderboard = ({
  title,
  btnTitle,
  btnUrl,
  logo,
  defaultTournamentId,
  tournaments,
}: HeaderLeaderboardProps) => {
  const { data: baseResponse, isInitialLoading } = useQuery({
    queryKey: ["tournament_base", defaultTournamentId],
    queryFn: () => {
      return axios.post(GOLF_PORTAL_API, {
        query: TournamentBase,
        variables: {
          tournamentId: defaultTournamentId,
        },
      });
    },
    enabled: !!defaultTournamentId,
    placeholderData: (prev: any) => prev,
  });
  const tournamentBaseData = baseResponse?.data?.data;

  const manualActiveTournament =
    tournaments &&
    tournaments?.find((t: Tournament) => t?.id === defaultTournamentId);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>(
    tournaments && tournaments?.length > 1
      ? manualActiveTournament
        ? manualActiveTournament?.id
        : tournaments?.[0]?.id
      : defaultTournamentId,
  );
  const [pendingTournamentId, setPendingTournamentId] = useState<string | null>(
    null,
  );

  const selectedTournament = useMemo(
    () => tournaments?.find((t) => t.id === selectedTournamentId),
    [tournaments, selectedTournamentId],
  );

  const isMultiRoundTournament =
    (!!selectedTournament?.rounds?.length &&
      selectedTournament?.rounds?.length > 1) ||
    (tournamentBaseData?.coursePlayTournament?.rounds &&
      tournamentBaseData?.coursePlayTournament?.rounds?.length > 1);

  const isRangeTournament =
    !!selectedTournament?.isRange || tournamentBaseData?.base?.isRange;
  const mode = isRangeTournament ? "range" : "stroke";

  const { data, isLoading: isLeaderboardDataLoading } = useQuery({
    queryKey: ["tournament_leaderboard_widget", selectedTournamentId, mode],
    queryFn: () => {
      return axios.post(GOLF_PORTAL_API, {
        query: isRangeTournament
          ? RangeTournamentLeaderboardWidget
          : TournamentLeaderboardWidget,
        variables: {
          tournamentId: selectedTournamentId,
          skip: 0,
          take: 5,
        },
      });
    },
    // wait for base data so range/stroke mode is known before querying
    enabled: !!selectedTournamentId && !isInitialLoading,
    placeholderData: (prev: any) => prev,
    staleTime: 5_000,
  });
  const leaderboardItems = data?.data?.data?.node?.leaderboard?.records?.items;

  const dropdownOptions =
    tournaments?.map((t) => ({
      label: t.name,
      id: t.id,
    })) ?? [];

  const onTournamentChange = (tournament: Option) => {
    setPendingTournamentId(tournament?.id as any);
  };

  const onDropdownAnimationComplete = () => {
    if (pendingTournamentId && pendingTournamentId !== selectedTournamentId) {
      setSelectedTournamentId(pendingTournamentId);
    }
  };

  return (
    <div className="flex flex-col items-center self-center pt-[30px] px-[15px] pb-5 mb-[15px] rounded-[10px] bg-tm-c-gray-950 lg:px-[25px] lg:pb-[30px] lg:mb-0 lg:mt-0 lg:w-fit">
      {logo?.filename && (
        <div className="relative w-[70px] h-[70px] lg:w-[85px] lg:h-[85px]">
          <Image
            src={logo.filename}
            alt={logo.alt}
            fill
            sizes="100vw"
            className="object-cover"
          />
        </div>
      )}
      <h3 className="m-0 mb-[15px] text-[15px] font-normal lg:text-[25px]">
        {tournaments && tournaments?.length > 1 ? "In Progress" : title}
      </h3>
      {isLeaderboardDataLoading && (
        <>
          {tournaments && tournaments?.length > 1 && (
            <Dropdown
              className="my-2.5 mb-6"
              disabled
              defaultValue={dropdownOptions[0]}
              options={dropdownOptions}
            />
          )}
          <div
            role="table"
            className="my-[15px] mx-0 w-full min-w-[290px] lg:my-[25px]"
          >
            <div
              role="thead"
              className={clsx(
                "bg-tm-c-gray-900 animate-pulse text-xs rounded-t-[10px] grid gap-5 py-1.5 pr-5 pl-4 lg:text-[15px] opacity-70",
                {
                  "grid-cols-[1fr_3fr_1fr_1fr]": !isRangeTournament,
                  "grid-cols-[1fr_3fr_2fr]": isRangeTournament,
                },
              )}
            >
              <div role="th">Pos</div>
              <div role="th">Player</div>
              {!isRangeTournament && <div role="th">Thru</div>}
              <div role="th">{isRangeTournament ? "Average" : "Score"}</div>
            </div>
            <div
              role="tbody"
              className="flex flex-col gap-[1px] text-[15px] leading-[25px] tracking-[0.45px]"
            >
              {Array.from(Array(5)).map((_item: any, i: number) => (
                <div
                  role="tr"
                  className={`bg-[rgba(65,65,65,0.9)] py-2.5 px-5 opacity-70 last:rounded-b-[10px] 
                            grid grid-cols-[1fr_3fr_1fr_1fr] gap-[15px]
                            [&>div:nth-child(2)]:w-[160px] [&>div:nth-child(2)]:whitespace-nowrap [&>div:nth-child(2)]:mask-[linear-gradient(to_right,var(--color-tm-c-dark)_60%,rgba(0,0,0,0)_100%)]
                          `}
                  key={`tr-key-${i}`}
                >
                  {Array.from(Array(4)).map((_item: any, i: number) => (
                    <div
                      role="td"
                      className="flex items-center"
                      key={`td-key-${i}`}
                    >
                      <span className="inline-block h-[25px] w-full bg-tm-c-dark opacity-30 rounded-[5px] animate-pulse" />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {!isLeaderboardDataLoading && tournaments && tournaments?.length > 1 && (
        <Dropdown
          className="my-2.5 mb-6"
          navClassName="shadow-[4px_4px_15px_rgba(0,0,0,0.3)]"
          defaultValue={
            dropdownOptions.find((o) => o.id === selectedTournamentId) ??
            dropdownOptions[0]
          }
          options={dropdownOptions}
          onChange={onTournamentChange}
          onAnimationComplete={onDropdownAnimationComplete}
        />
      )}
      {!isLeaderboardDataLoading && leaderboardItems?.length > 0 && (
        <div
          role="table"
          className="mx-0 w-full min-w-[290px] lg:min-w-[405px] lg:my-[25px]"
        >
          <div
            role="thead"
            className={clsx(
              "bg-tm-c-gray-900 text-xs rounded-t-[10px] grid gap-5 py-1.5 pr-5 pl-4 lg:text-[15px]",
              {
                "grid-cols-[1fr_3fr_1fr_1fr]": !isRangeTournament,
                "grid-cols-[1fr_3fr_2fr]": isRangeTournament,
              },
            )}
          >
            <div role="th">Pos</div>
            <div role="th">Player</div>
            {!isRangeTournament && <div role="th">Thru</div>}
            <div role="th">{isRangeTournament ? "Average" : "Score"}</div>
          </div>
          <div
            role="tbody"
            className="flex flex-col gap-[1px] text-[15px] leading-[25px] tracking-[0.45px]"
          >
            {leaderboardItems.map((item: any, playerIndex: number) => {
              const isPlayerInSameGroup =
                playerIndex > 0 &&
                item?.total?.pos !==
                  leaderboardItems[playerIndex - 1]?.total?.pos;

              const isPlayerInSameGroupRange =
                playerIndex > 0 &&
                item?.score?.pos !==
                  leaderboardItems[playerIndex - 1]?.score?.pos;

              const isPosFirst = isRangeTournament
                ? isPlayerInSameGroupRange
                : isPlayerInSameGroup;

              const convertMToFeet = (distanceInMeters: number) => {
                if (distanceInMeters === null) return "-";
                const inches = distanceInMeters * 39.37;
                const feet = Math.floor(inches / 12);
                const remainingInches = Math.floor(inches % 12);
                return `${feet}' ${remainingInches}"`;
              };

              return (
                <div
                  key={`${item.id}-${playerIndex}`}
                  role="tr"
                  className={clsx(
                    `grid gap-[15px]
                              bg-[rgba(65,65,65,0.9)] py-2.5 px-5 last:rounded-b-[10px]
                              [&>div:nth-child(2)]:w-[160px] [&>div:nth-child(2)]:whitespace-nowrap [&>div:nth-child(2)]:mask-[linear-gradient(to_right,var(--color-tm-c-dark)_60%,rgba(0,0,0,0)_100%)]
                            `,
                    {
                      "grid-cols-[1fr_3fr_1fr_1fr]": !isRangeTournament,
                      "grid-cols-[1fr_3fr_2fr]": isRangeTournament,
                    },
                  )}
                >
                  <div role="td">
                    {item.total.state.toLowerCase() === "did_not_finish" ||
                    item.total.state.toLowerCase() === "no_show"
                      ? "DNF"
                      : isPosFirst || playerIndex === 0
                        ? isRangeTournament
                          ? item?.score?.pos
                          : item?.total?.pos
                        : ""}
                  </div>
                  <div role="td">{item.playername}</div>
                  {!isRangeTournament && (
                    <div role="td">
                      {item.total.thru === 18 ? "F" : item.total.thru}
                      {isMultiRoundTournament && (
                        <span
                          className={clsx({
                            "opacity-60": item?.latestRound?.roundNumber,
                          })}
                        >
                          {item?.latestRound?.roundNumber
                            ? `(R${item?.latestRound?.roundNumber})`
                            : "-"}
                        </span>
                      )}
                    </div>
                  )}
                  <div role="td">
                    {isRangeTournament && (
                      <span className="flex flex-row text-center">
                        {convertMToFeet(
                          item?.average?.distanceToPinWithPenalty,
                        )}
                      </span>
                    )}
                    {!isRangeTournament && (
                      <>
                        {item.total.toPar < 0 && (
                          <span className="bg-tm-c-brand inline-block w-[35px] lg:w-[45px] rounded-[3px] text-center">
                            {item.total.toPar}
                          </span>
                        )}
                        {item.total.toPar == 0 && (
                          <span className="bg-[#8e8e8e] inline-block w-[35px] lg:w-[45px] rounded-[3px] text-center">
                            E
                          </span>
                        )}
                        {item.total.toPar > 0 && (
                          <span className="bg-tm-c-dark inline-block w-[35px] lg:w-[45px] rounded-[3px] text-center">
                            +{item.total.toPar}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {btnTitle && (
        <Link
          href={
            selectedTournament
              ? `${getTournamentPath(selectedTournament)}/leaderboard`
              : btnUrl
          }
          className={`text-[15px] 2xl:text-[17px] cursor-pointer text-center
              rounded-[6px] w-fit min-h-[40px] py-0 px-10 2xl:h-[45px]
              flex items-center justify-center
              disabled:opacity-30 disabled:cursor-default
              mt-6 self-center lg:self-auto
              text-tm-c-light bg-tm-c-brand
              `}
        >
          {btnTitle}
        </Link>
      )}
    </div>
  );
};

export default HeaderLeaderboard;
